import HomeWorkRoundedIcon from '@mui/icons-material/HomeWorkRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import HelpOutlineRoundedIcon from '@mui/icons-material/HelpOutlineRounded';
import PriorityHighRoundedIcon from '@mui/icons-material/PriorityHighRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import BuildingDataGridToolbar from './BuildingDataGridToolbar';
import BuildingEnergySavingsDialog from '../dialog/BuildingEnergySavingsDialog';
import {
  Badge,
  Box,
  Button,
  Chip,
  IconButton,
  ListItemText,
  Typography,
} from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { useLocalStorageJson } from '@/hooks/useLocalStorageJson';
import { useMemo, useState } from 'react';
import { daDK } from '@mui/x-data-grid/locales';

import BuildingActionsCell from './BuildingActionsCell';
import DataGridNoRowsOverlay from '@/components/datagrid/DataGridNoRowsOverlay';

import { darken, lighten } from '@mui/material/styles';

const energySavingsVisual = (es) => {
  const errorCount = es?.Errors?.length ?? 0;
  const warningCount = es?.Warnings?.length ?? 0;

  if (errorCount > 0) {
    return {
      severity: 'error',
      colorKey: 'error',
      count: errorCount,
      Icon: ErrorRoundedIcon,
      tooltip: `${errorCount} error${errorCount > 1 ? 's' : ''}${
        warningCount
          ? ` · ${warningCount} warning${warningCount > 1 ? 's' : ''}`
          : ''
      }`,
    };
  }
  if (warningCount > 0) {
    return {
      severity: 'warning',
      colorKey: 'warning',
      count: warningCount,
      Icon: WarningRoundedIcon,
      tooltip: `${warningCount} warning${warningCount > 1 ? 's' : ''}`,
    };
  }
  return {
    severity: 'none',
    colorKey: 'default',
    count: 0,
    Icon: null,
    tooltip: 'No issues',
  };
};

// put these helpers near the top of BuildingDataGrid.jsx
const BM_SEVERITY = ['Unknown', 'Success', 'Warning', 'Error', 'Critical'];
const bmNorm = (s) => {
  if (!s || typeof s !== 'string') return 'Unknown';
  const x = s.trim().toLowerCase();
  if (x.startsWith('succ')) return 'Success';
  if (x.startsWith('warn')) return 'Warning';
  if (x === 'error' || x === 'err') return 'Error';
  if (x.startsWith('crit')) return 'Critical';
  return 'Unknown';
};
const bmWorst = (arr) =>
  arr.reduce(
    (w, s) =>
      BM_SEVERITY.indexOf(bmNorm(s)) > BM_SEVERITY.indexOf(w) ? bmNorm(s) : w,
    'Unknown',
  );
const collectBMStatuses = (bm) => {
  if (!bm) return [];
  const out = [];
  if (bm.OverallStatus) out.push(bm.OverallStatus);
  (bm.BillingMeterHealthChecks || []).forEach((c) => {
    if (c.DataInputStatus) out.push(c.DataInputStatus);
    (c.DataInputHealthChecks || []).forEach(
      (sc) => sc.Status && out.push(sc.Status),
    );
  });
  return out;
};
const billingStatusVisual = (bm) => {
  const worst = bmWorst(collectBMStatuses(bm));
  switch (worst) {
    case 'Success':
      return { colorKey: 'success', Icon: CheckCircleRoundedIcon };
    case 'Warning':
      return { colorKey: 'warning', Icon: WarningRoundedIcon };
    case 'Error':
      return { colorKey: 'error', Icon: ErrorRoundedIcon };
    case 'Critical':
      return { colorKey: 'error', Icon: PriorityHighRoundedIcon };
    default:
      return { colorKey: 'default', Icon: HelpOutlineRoundedIcon };
  }
};

const getTrendColor = (percentage) => {
  if (percentage > 5) {
    return 'success';
  } else if (percentage === 0) {
    return 'default';
  } else if (percentage <= 5 && percentage >= 0) {
    return 'warning';
  } else if (percentage < 0) {
    return 'error';
  } else {
    return 'error';
  }
};

export default function BuildingDataGrid({
  buildings,
  accounts,
  onCreateBuilding,
  onEditBuilding,
  onEditBillingMeters,
  onViewBuilding,
}) {
  const [openEnergySavingsDialog, setOpenEnergySavingsDialog] = useState(false);
  const [energySavings, setEnergySavings] = useState(null);
  const [energySavingsPreviousYears, setEnergySavingsPreviousYears] =
    useState(null);
  const enrichedBuildings = useMemo(() => {
    if (!accounts || !buildings) return [];

    // Make a lookup for accounts by their id
    const accountMap = new Map(
      accounts.map((account) => [account.AccountId, account]),
    );

    return buildings.map((building) => ({
      ...building,
      Account: accountMap.get(building.AccountId) || null, // attach matching account
    }));
  }, [accounts, buildings]);

  const [dataGridState, setDataGridState] = useLocalStorageJson(
    `Building-DataGrid-Presets`,
    {
      presets: [
        {
          name: 'Default',
          visibilityList: {},
          density: 'standard',
          sortModel: [],
          filterModel: { items: [], quickFilterValues: [] },
        },
      ],
      chosenPreset: 0,
    },
  );

  const handleBuildingEnergySavingsClick = (building) => {
    setOpenEnergySavingsDialog(true);
    setEnergySavings(building.state.EnergySavings || null);
    setEnergySavingsPreviousYears(
      building.state.EnergySavingsPreviousYears || null,
    );
  };

  const updateChosenPreset = (patch) =>
    setDataGridState((prev) => ({
      ...prev,
      presets: prev.presets.map((preset, idx) =>
        idx === prev.chosenPreset ? { ...preset, ...patch } : preset,
      ),
    }));

  const handleVisibilityChange = (model) => {
    updateChosenPreset({ visibilityList: model });
  };

  const handleDensityChange = (newDensity) => {
    updateChosenPreset({ density: newDensity });
  };

  const handleSortModelChange = (newModel) => {
    updateChosenPreset({ sortModel: newModel });
  };

  const handleFilterModelChange = (newModel) => {
    updateChosenPreset({ filterModel: newModel });
  };

  const currentPreset = dataGridState.presets[dataGridState.chosenPreset];
  const rows = useMemo(() => {
    if (!enrichedBuildings) return [];
    return enrichedBuildings.map((b) => ({
      id: b.BuildingId,
      BuildingId: b.BuildingId,
      Name: b.Name,
      AccountId: b.AccountId,
      accountName: b.Account?.Name || 'No account',
      state: b.State,
      billingMeters: b.BillingMeters || [],
      billingMeterCount: b.BillingMeters?.length || null,
      address: b.Location?.Address || 'No address',
      latitude: b.Location?.Latitude || null,
      longitude: b.Location?.Longitude || null,
      location: b.Location || {},
      baselineDayInterval: b.BaselineDayInterval || 0,
      allowSaturdayBaselineDay: b.AllowSaturdayBaselineDay || false,
      JobSubscriptions: b.JobSubscriptions || [],
      userInput: b.UserInput || {},
      energySavingsPercentage:
        b.State?.EnergySavings?.Data?.EnergySavingsSummary
          ?.TotalEnergySavingsPercentage || null,
    }));
  }, [enrichedBuildings]);

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
    },
    {
      field: 'Name',
      headerName: 'Name',
      flex: 3,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ListItemText
            primary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <HomeWorkRoundedIcon fontSize="small" />
                {params.row.Name || '-'}
              </Box>
            }
            secondary={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlaceRoundedIcon fontSize="small" />{' '}
                {params.row.address || '-'}
              </Box>
            }
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
          />
        </Box>
      ),
    },
    {
      field: 'AccountId',
      headerName: 'Account ID',
      flex: 1,
    },
    {
      field: 'accountName',
      headerName: 'Account',
      flex: 2,
    },
    {
      field: 'baselineDayInterval',
      headerName: 'Baseline Day Interval',
      flex: 1,
      renderCell: (params) => (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
          }}
        >
          <ListItemText
            primary={`Every ${params.value || 'Not set'} days`}
            secondary={
              params.row.allowSaturdayBaselineDay ? (
                <Typography fontStyle={'italic'} sx={{ fontSize: '0.875rem' }}>
                  Includes Saturdays
                </Typography>
              ) : (
                <Typography fontStyle={'italic'} sx={{ fontSize: '0.875rem' }}>
                  Exclude Saturdays
                </Typography>
              )
            }
          />
        </Box>
      ),
    },
    {
      field: 'energySavingsPercentage',
      headerName: 'Energy Savings',
      flex: 1,
      renderCell: (params) => {
        if (!params.row.energySavingsPercentage)
          return (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <Typography>-</Typography>
            </Box>
          );
        const es = params.row?.state?.EnergySavings;
        const vis = energySavingsVisual(es);
        return (
          <Box sx={{}}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                vis.Icon ? (
                  <Box
                    sx={{ display: 'inline-flex', alignItems: 'center' }}
                    title={vis.tooltip}
                  >
                    <vis.Icon
                      sx={{
                        fontSize: 14,
                        color: (theme) =>
                          vis.colorKey === 'default'
                            ? theme.palette.text.disabled
                            : theme.palette[vis.colorKey].main,
                      }}
                    />
                    {/* Optional: show the count next to the icon */}
                    {/* <Typography variant="caption" sx={{ ml: 0.25, fontWeight: 700 }}>
                      {vis.count}
                    </Typography> */}
                  </Box>
                ) : null
              }
              invisible={vis.severity === 'none'}
            >
              <Chip
                component={Button}
                onClick={() => handleBuildingEnergySavingsClick(params.row)}
                sx={{
                  marginLeft: 1,
                  ':hover': { cursor: 'pointer' },
                  width: 75,
                  fontWeight: 'bold',
                  backgroundColor: (theme) =>
                    theme.palette.mode === 'light'
                      ? lighten(
                          theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].main,
                          0.9,
                        )
                      : darken(
                          theme.palette[
                            getTrendColor(params.row.energySavingsPercentage)
                          ].main,
                          0.8,
                        ),
                  color: (theme) =>
                    theme.palette[
                      getTrendColor(params.row.energySavingsPercentage)
                    ].light,
                  border: (theme) =>
                    `1px solid ${
                      theme.palette[
                        getTrendColor(params.row.energySavingsPercentage)
                      ].light
                    }`,
                }}
                label={
                  params.row.energySavingsPercentage
                    ? `${params.row.energySavingsPercentage.toFixed(1)}%`
                    : '0%'
                }
                size={'small'}
              />
            </Badge>
          </Box>
        );
      },
    },
    {
      field: 'billingMeterCount',
      headerName: 'Billing Meters',
      flex: 1,
      renderCell: (params) => {
        const count = params.row?.billingMeterCount ?? 0;
        const bm = params.row?.state?.BillingMeterHealthCheck;
        const vis = billingStatusVisual(bm);

        if (count === 0) {
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
              <Typography variant="body2" color="text.secondary">
                No billing meter found
              </Typography>
            </Box>
          );
        }

        return (
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <vis.Icon
                sx={{
                  fontSize: 14,
                  color: (theme) =>
                    vis.colorKey === 'default'
                      ? theme.palette.text.disabled
                      : theme.palette[vis.colorKey].main,
                }}
              />
            }
          >
            <IconButton
              size="small"
              sx={{ border: 1, borderColor: 'divider' }}
              onClick={() => onEditBillingMeters(params.row)}
            >
              <SpeedRoundedIcon />
            </IconButton>
          </Badge>
        );
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      flex: 1,
      renderCell: (params) => {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <BuildingActionsCell
              row={params.row}
              onView={(row) => onViewBuilding(row)}
              onEdit={(row) => onEditBuilding(row)}
            />
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <DataGridPremium
        key={dataGridState.chosenPreset}
        localeText={daDK.components.MuiDataGrid.defaultProps.localeText}
        rows={rows}
        columns={columns}
        columnHeaderHeight={36}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        hideFooterSelectedRowCount
        columnVisibilityModel={currentPreset.visibilityList}
        onColumnVisibilityModelChange={handleVisibilityChange}
        onDensityChange={(density) => handleDensityChange(density)}
        density={currentPreset.density}
        sortModel={currentPreset.sortModel}
        onSortModelChange={handleSortModelChange}
        filterModel={currentPreset.filterModel}
        onFilterModelChange={handleFilterModelChange}
        slots={{
          toolbar: BuildingDataGridToolbar,
          noRowsOverlay: DataGridNoRowsOverlay,
        }}
        slotProps={{
          toolbar: {
            showQuickFilter: true,
            onCreateBuilding: onCreateBuilding,
            // handleOpen: handleOpen,
            // filterState, // Pass filterState here
            // setFilterState, // Pass setFilterState here
            dataGridState: dataGridState,
            setDataGridState: setDataGridState,
            // setOpenHeatingPanicDialog: setOpenHeatingPanicDialog,
          },
          // noRowsOverlay: {
          //   rows: 'Bygninger',
          //   // isLoading: isLoading,
          // },
        }}
        sx={{
          borderRadius: 2,
          border: 'none',
          maxHeight: '85vh',
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within':
            {
              outline: 'none',
            },
          '& .hidden-column-cell, & .hidden-column-header': {
            display: 'none',
          },
          '& .MuiDatagrid-root': {
            border: 0,
          },
          '.MuiDataGrid-withBorderColor': {
            border: 'none !important',
          },
          '& .MuiDatagrid-footerContainer': {
            border: 0,
          },
          '& .MuiDatagrid-bottomContainer': {
            border: 0,
          },
          '& .MuiDataGrid-main': {
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? '#fff' : '#121212',
            borderRadius: 2,
            border: (theme) => `1px solid ${theme.palette.divider}`,
          },
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid', // Optional: Add a border
            borderColor: 'divider',
          },
          '& .MuiDataGrid-columnsContainer': {
            backgroundColor: 'blue',
          },
          '& .selected-row': {
            backgroundColor: (theme) => theme.palette.action.selected,
            '&:hover': {
              backgroundColor: (theme) => theme.palette.action.selected,
            },
          },
          '& .MuiDataGrid-scrollbar': {
            overflow: 'hidden !important',
          },
        }}
      />

      <BuildingEnergySavingsDialog
        open={openEnergySavingsDialog}
        onClose={() => setOpenEnergySavingsDialog(false)}
        energySavings={energySavings}
        energySavingsPreviousYears={energySavingsPreviousYears}
      />
    </>
  );
}
